import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE, LANGUAGE_MAP } from '@/common/constants';
import { Configuration, GroupConfiguration } from '@nylas/core';

/**
 * The `nylas-disable-emails` component is a UI component that allows users to select the event type for the scheduler configuration.
 */
@Component({
  tag: 'nylas-disable-emails',
  styleUrl: 'nylas-disable-emails.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasDisableEmails {
  @Element() host!: HTMLNylasDisableEmailsElement;
  /**
   * @standalone
   * The selected config
   */
  @Prop() selectedConfiguration?: Configuration | GroupConfiguration;

  /**
   * @standalone
   * The name of the component
   */
  @Prop() name: string = 'disable-emails';

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * The organizer locale.
   */
  @State() organizerLocale: string = this.selectedLanguage || 'en';

  /**
   * The disable emails value.
   */
  @State() disableEmails: boolean = false;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * This event is fired when the toggle switch to disable emails is toggled.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
    valueChanged?: (event: CustomEvent<{ value: string; name: string }>) => void;
  }>;

  // Lifecycle methods
  connectedCallback() {
    debug('nylas-disable-emails', 'connectedCallback');
  }

  disconnectedCallback() {
    debug('nylas-disable-emails', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-disable-emails', 'componentWillLoad');
    // See comment in the @Watch('name') decorator for more information.
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-disable-emails', 'componentDidLoad');
    const lang = (this.selectedConfiguration as Configuration)?.scheduler?.email_template?.organizer_locale || this.selectedLanguage || 'en';
    this.organizerLocale = lang;
    const isGroupConfig = (this.selectedConfiguration as GroupConfiguration)?.group_booking !== undefined;
    const disableEmails = isGroupConfig
      ? (this.selectedConfiguration as GroupConfiguration)?.group_booking?.disable_emails
      : (this.selectedConfiguration as Configuration)?.event_booking?.disable_emails;
    this.disableEmails = disableEmails || false;
    this.handleSettingValue(false);
  }

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-disable-emails', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('selectedConfiguration')
  selectedConfigurationChangedHandler(newVal: Configuration | GroupConfiguration, oldVal: Configuration | GroupConfiguration) {
    if (newVal === oldVal) return;
    this.organizerLocale = newVal.scheduler?.email_template?.organizer_locale || this.selectedLanguage || 'en';
    this.handleSettingValue(false);
    const isGroupConfig = (newVal as GroupConfiguration)?.group_booking !== undefined;
    const disableEmails = isGroupConfig ? (newVal as GroupConfiguration)?.group_booking?.disable_emails : (newVal as Configuration)?.event_booking?.disable_emails;
    this.disableEmails = disableEmails || false;
    this.handleSettingValue(false);
  }

  @Listen('nylasFormSwitchToggled')
  nyalsFormSwitchToggledHandler(event: CustomEvent<{ checked: boolean; name: string; label: boolean }>) {
    debug('nylas-confirmation-email', 'nyalsFormSwitchToggledHandler', event.detail);
    const { checked } = event.detail;
    if (event.detail.name === 'disable-emails') {
      this.disableEmails = !checked;
      this.handleSettingValue();
    }
  }

  @Listen('nylasFormDropdownChanged')
  nylasFormDropdownChangedHandler(
    event: CustomEvent<{
      value: string;
      name: string;
    }>,
  ) {
    const { name, value } = event.detail;
    if (name === 'organizer-locale') {
      const lang = LANGUAGE_CODE[value as LANGUAGE_CODE];
      this.organizerLocale = lang;
      this.handleSettingValue();
    }
  }

  handleSettingValue(valueChanged: boolean = true) {
    if (valueChanged) {
      this.valueChanged.emit({
        value: JSON.stringify({
          disableEmails: this.disableEmails,
          organizerLocale: this.organizerLocale,
        }),
        name: this.name,
      });
    }
    this.internals.setFormValue(
      JSON.stringify({
        disableEmails: this.disableEmails,
        organizerLocale: this.organizerLocale,
      }),
      this.name,
    );
  }

  @RegisterComponent<NylasDisableEmails, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-disable-emails',
    stateToProps: new Map([
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
    ]),
    eventToProps: {
      valueChanged: async (
        event: CustomEvent<{ value: string; name: string; valueChanged?: (event: CustomEvent<{ value: string; name: string }>) => void }>,
        _nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { valueChanged } = event.detail;
        // If a handler is passed, call it.
        if (valueChanged) {
          valueChanged(event);
        }
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    const languageOptions = Object.keys(LANGUAGE_MAP).map(key => ({
      label: LANGUAGE_MAP[key],
      value: key,
    }));
    const isGroupConfig = (this.selectedConfiguration as GroupConfiguration)?.group_booking !== undefined;
    const disableEmails = isGroupConfig
      ? (this.selectedConfiguration as GroupConfiguration)?.group_booking?.disable_emails
      : (this.selectedConfiguration as Configuration)?.event_booking?.disable_emails;
    return (
      <Host>
        <div class="nylas-disable-emails" part="nde">
          <div class="header" part="nde__header">
            <div>
              <h3>{i18next.t('eventCommunicationsTitle')}</h3>
              <p>{i18next.t('eventCommunicationsDescription')}</p>
            </div>
          </div>
          <div class="toggle" part="nde__toggle">
            <label>
              {i18next.t('nylasConfirmationEmail.confirmationEmail.label')}
              <span class="label-icon">
                <tooltip-component>
                  <info-icon slot="tooltip-icon" />
                  <span slot="tooltip-content"> {i18next.t('nylasConfirmationEmail.confirmationEmail.tooltip.desc')}</span>
                </tooltip-component>
              </span>
            </label>
            <toggle-switch exportparts="ts_label: nde__toggle-label, ts_input: nde_toggle-input, ts_slider: nde_toggle-slider" name={`disable-emails`} checked={!disableEmails} />
          </div>
          <div class="toggle" part="nde__toggle">
            <label>
              {i18next.t('nylasConfirmationEmail.confirmationEmail.language')}
              <span class="label-icon">
                <tooltip-component>
                  <info-icon slot="tooltip-icon" />
                  <span slot="tooltip-content"> {i18next.t('nylasConfirmationEmail.confirmationEmail.tooltip.language')}</span>
                </tooltip-component>
              </span>
            </label>
            <select-dropdown
              name="organizer-locale"
              options={languageOptions}
              searchPlaceholder={i18next.t('search')}
              withChevron={false}
              defaultSelectedOption={languageOptions.find(lang => lang.value == this.organizerLocale)}
              exportparts="sd_dropdown: nse__language-dropdown, sd_dropdown-button: nse__language-drop-button, sd_dropdown-content: nse__language-drop-content, sd_dropdown_label: nse__language-drop-label"
            >
              <span slot="select-icon">
                <translate-icon width="20" height="20" />
              </span>
            </select-dropdown>
          </div>
        </div>
      </Host>
    );
  }
}
